import React, { useState, useEffect } from 'react';
import { DollarSign, ShoppingBag, Users, Store, TrendingUp } from 'lucide-react';
import { productService } from '../../services/productService';
import { vendorService } from '../../services/vendorService';
import { orderService } from '../../services/orderService';
import { userService } from '../../services/userService';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    totalUsers: 0,
    totalVendors: 0,
    totalProducts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [products, vendors, orders, users] = await Promise.all([
        productService.getAllProducts({ inventory: true }),
        vendorService.getAllVendors(),
        orderService.getAllOrders(),
        userService.getAllUsers()
      ]);

      const totalRevenue = orders.data.reduce((sum, order) => sum + order.totalPrice, 0);

      setStats({
        totalRevenue,
        totalOrders: orders.count,
        totalUsers: users.count,
        totalVendors: vendors.count,
        totalProducts: products.count
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Revenue',
      value: stats.totalRevenue.toFixed(2),
      icon: DollarSign,
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Total Orders',
      value: stats.totalOrders,
      icon: ShoppingBag,
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      color: 'from-purple-500 to-pink-600'
    },
    {
      title: 'Total Vendors',
      value: stats.totalVendors,
      icon: Store,
      color: 'from-orange-500 to-red-600'
    }
  ];

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-600 mt-2">Overview of your e-commerce platform</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-gradient-to-r ₹{stat.color}`}>
                <stat.icon className="text-white" size={24} />
              </div>
              <TrendingUp className="text-green-500" size={20} />
            </div>
            <h3 className="text-gray-600 text-sm font-medium">{stat.title}</h3>
            <p className="text-3xl font-bold text-gray-800 mt-2">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Stats</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl">
              <span className="text-gray-700">Total Products</span>
              <span className="font-bold text-purple-600">{stats.totalProducts}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
              <span className="text-gray-700">Average Order Value</span>
              <span className="font-bold text-green-600">
                ₹{stats.totalOrders > 0 ? (stats.totalRevenue / stats.totalOrders).toFixed(2) : '0.00'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Activity</h2>
          <div className="space-y-3">
            <p className="text-gray-600">System is running smoothly</p>
            <p className="text-sm text-gray-500">All services operational</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;